import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { BLOG_POSTS } from "@/data/blog";

const fmt = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

export default function FeaturedArticles() {
  const [lead, ...rest] = BLOG_POSTS;
  const side = rest.slice(0, 3);

  return (
    <section id="insights" className="relative py-24 lg:py-32 bg-[#05050A] border-t border-white/10" data-testid="featured-articles">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <span className="text-xs uppercase tracking-[0.3em] text-[#22D3EE]">From the blog</span>
            <h2 className="mt-5 font-display font-900 text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-white leading-none">
              Latest insights
            </h2>
          </div>
          <Link to="/blog" data-testid="view-all-articles" className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-white hover:text-[#22D3EE] transition-colors duration-300">
            View all articles
            <ArrowUpRight className="w-4 h-4 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform duration-300" />
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Lead article */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <Link to={`/blog/${lead.slug}`} data-testid={`featured-lead-${lead.slug}`} className="group block rounded-2xl border border-white/10 bg-[#0A0A12] overflow-hidden hover:border-[#22D3EE]/40 transition-colors duration-300 h-full">
              <div className="relative aspect-[16/9] overflow-hidden">
                <img src={lead.image} alt={lead.title} className="w-full h-full object-cover opacity-60 group-hover:opacity-85 group-hover:scale-105 transition-all duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A12] via-transparent to-transparent" />
                <span className="absolute top-5 left-5 text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full bg-black/50 backdrop-blur border border-white/10" style={{ color: lead.accent }}>
                  {lead.serviceNav}
                </span>
              </div>
              <div className="p-8">
                <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.15em] text-slate-500">
                  <span>{fmt(lead.date)}</span><span className="text-slate-700">·</span><span>{lead.readTime}</span>
                </div>
                <h3 className="mt-4 font-display font-700 text-2xl lg:text-3xl text-white tracking-tight leading-tight group-hover:text-[#22D3EE] transition-colors duration-300">
                  {lead.title}
                </h3>
                <p className="mt-4 text-sm text-slate-400 leading-relaxed max-w-xl">{lead.excerpt}</p>
              </div>
            </Link>
          </motion.div>

          {/* Side list */}
          <div className="flex flex-col gap-5">
            {side.map((p, i) => (
              <motion.div
                key={p.slug}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="flex-1"
              >
                <Link to={`/blog/${p.slug}`} data-testid={`featured-side-${p.slug}`} className="group flex items-center gap-5 rounded-2xl border border-white/10 bg-[#0A0A12] p-4 pr-6 hover:border-[#22D3EE]/40 transition-colors duration-300 h-full">
                  <div className="relative w-28 h-24 flex-shrink-0 rounded-xl overflow-hidden">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover opacity-60 group-hover:opacity-90 transition-opacity duration-500" />
                  </div>
                  <div className="min-w-0">
                    <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: p.accent }}>{p.serviceNav}</span>
                    <h3 className="mt-1.5 font-display font-700 text-base lg:text-lg text-white tracking-tight leading-snug group-hover:text-[#22D3EE] transition-colors duration-300 line-clamp-2">
                      {p.title}
                    </h3>
                    <span className="mt-1.5 block text-[11px] uppercase tracking-[0.15em] text-slate-500">{p.readTime}</span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
